import React, { useCallback, useMemo } from 'react'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import { CardFooter, FieldSection, LabeledSelect, LabeledText } from '../fieldPrimitives'
import { EDUCATION_LEVEL_OPTIONS } from './patientRecordOptions'
import { EDUCATION_SCHEMA } from './patientRecordSchema'
import { makeFieldSetter, useCardAutosave } from './useCardAutosave'
import { usePatientRecord } from './usePatientRecord'
import { useUpdatePatientRecord } from './useUpdatePatientRecord'

type EducationValues = { educationLevel: string; otherEducation: string }

const EducationPanel = () => {
  const uid = usePanelPatientUid()
  const { loading, patient } = usePatientRecord(uid)
  const update = useUpdatePatientRecord(uid)

  const initial = useMemo<EducationValues>(
    () => ({ educationLevel: patient?.educationLevel ?? '', otherEducation: patient?.otherEducation ?? '' }),
    [patient?.educationLevel, patient?.otherEducation]
  )

  const save = useCallback(
    (next: EducationValues) =>
      update({ educationLevel: next.educationLevel, otherEducation: next.otherEducation }, undefined),
    [update]
  )

  const { values, setValues, error, status } = useCardAutosave(initial, EDUCATION_SCHEMA, save)
  const set = makeFieldSetter(setValues)

  const isOtherSelected = values.educationLevel === 'OTHER'

  return (
    <FieldSection>
      <LabeledSelect
        label="Level of education"
        value={values.educationLevel}
        onChange={set('educationLevel')}
        options={EDUCATION_LEVEL_OPTIONS}
      />
      {isOtherSelected && (
        <LabeledText
          label="Other education completed"
          value={values.otherEducation}
          onChange={set('otherEducation')}
          multiline
        />
      )}
      <CardFooter loading={loading} status={status} error={error} />
    </FieldSection>
  )
}

export default EducationPanel
